import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { H as HistoryService, S as StringService } from './index2.js';
import { T as TrackingService, D as DomService } from './index3.js';
import { T as TranslationService } from './index6.js';
import { r as remCalc } from './index5.js';
import { A as AdobeTracking } from './index7.js';
import { d as defineCustomElement$3 } from './roche-icon2.js';
import { d as defineCustomElement$2 } from './roche-offcanvas-trigger2.js';

const rocheNavigationMobileCss = "roche-navigation-mobile{margin-top:0;display:block;box-sizing:border-box;max-width:100%}@media (min-width: 64rem){roche-navigation-mobile{margin-top:0}}roche-navigation-mobile ul,roche-navigation-mobile li{margin:0;padding:0;list-style:none}roche-navigation-mobile a,roche-navigation-mobile span{text-decoration:none;color:#544f4f;fill:#544f4f}roche-navigation-mobile span{pointer-events:none;display:flex;justify-content:space-between;align-items:center}roche-navigation-mobile span roche-icon{transition:transform 150ms ease-in;transform:rotate3d(0, 0, 1, 0deg)}roche-navigation-mobile .open span roche-icon{transform:rotate3d(0, 0, 1, 180deg)}roche-navigation-mobile .header{display:flex;flex-direction:row-reverse;justify-content:space-between;align-items:center;padding:1rem 1.25rem;border-bottom:1px solid #dbd6d1}roche-navigation-mobile .header__close{color:#544f4f;fill:#544f4f}roche-navigation-mobile .header__link{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;display:flex;justify-content:flex-start;align-items:center}@media (min-width: 48rem){roche-navigation-mobile .header__link{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}roche-navigation-mobile .header__link-icon{padding:0 0.875rem 0 0}roche-navigation-mobile nav{padding:0 1.25rem 3rem}roche-navigation-mobile nav>ul{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:400;font-size:1.5rem;line-height:1.9375rem;padding:0 0 1rem;margin:2rem 0 0;border-bottom:1px solid #dbd6d1}roche-navigation-mobile nav>ul strong{font-weight:600}@media (min-width: 48rem){roche-navigation-mobile nav>ul{font-size:1.5rem;line-height:2.125rem}}@media (min-width: 90rem){roche-navigation-mobile nav>ul{font-size:1.875rem;line-height:2.5625rem}}roche-navigation-mobile nav>ul+ul{margin:1.5rem 0 0}roche-navigation-mobile nav>ul>li{transform:translate3d(0, 0, 0);transition:height 150ms ease-in 150ms}roche-navigation-mobile nav>ul>li ul{transition:opacity 150ms ease-in 0ms;opacity:0;pointer-events:none}roche-navigation-mobile nav>ul>li.open{transition:height 150ms ease-in}roche-navigation-mobile nav>ul>li.open ul{transition:opacity 150ms ease-in 150ms;opacity:1;pointer-events:inherit}roche-navigation-mobile nav>ul>li:not(:first-child){margin:1.5rem 0 0}roche-navigation-mobile ul ul{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;opacity:0}@media (min-width: 64rem){roche-navigation-mobile ul ul{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}roche-navigation-mobile ul>li>ul>li{margin:0;padding:0.625rem 0 0.625rem 2rem;border-left:1px solid #dbd6d1}roche-navigation-mobile ul>li>ul>li>a{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;font-weight:400;color:#544f4f}@media (min-width: 64rem){roche-navigation-mobile ul>li>ul>li>a{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}roche-navigation-mobile ul>li>ul>li:first-child{padding:1.5rem 0 0.625rem;border-left:none}roche-navigation-mobile ul>li>ul>li:first-child>a{display:flex;align-items:center;color:#000}roche-navigation-mobile ul>li>ul>li:first-child>a roche-icon{margin:0 0 0 0.5rem}roche-navigation-mobile ul>li>ul>li:last-child{padding:0.625rem 0 0 2rem}roche-navigation-mobile .current-page{border-color:var(--color-interactive--primary-idle)}roche-navigation-mobile .current-page a{color:var(--color-interactive--primary-idle)}roche-navigation-mobile[dir=rtl] .header__link-icon{padding:0 0 0 0.875rem}roche-navigation-mobile[dir=rtl] ul>li>ul>li:not(:first-child){padding:0.625rem 2rem 0.625rem 0;border-right:1px solid #dbd6d1;border-left:none}roche-navigation-mobile[dir=rtl] ul>li>ul>li:not(:first-child):first-child{border-right:none}roche-navigation-mobile[dir=rtl] ul>li>ul>li:not(:first-child):first-child roche-icon{margin:0 0.5rem 0 0}";
const RocheNavigationMobileStyle0 = rocheNavigationMobileCss;

const OPEN = 'open';
const isNavigationHtmlElement = (element) => (element === null || element === void 0 ? void 0 : element.localName) === 'nav';
const closeItem = (item) => {
    var _a, _b;
    const height = (item === null || item === void 0 ? void 0 : item.scrollHeight) - ((_b = (_a = item.querySelector('ul')) === null || _a === void 0 ? void 0 : _a.scrollHeight) !== null && _b !== void 0 ? _b : 0);
    item === null || item === void 0 ? void 0 : item.setAttribute('style', `height: ${height}px`);
    item === null || item === void 0 ? void 0 : item.classList.remove(OPEN);
};
const openItem = (item) => {
    const height = item === null || item === void 0 ? void 0 : item.scrollHeight;
    item === null || item === void 0 ? void 0 : item.setAttribute('style', `height: ${height}px`);
    item === null || item === void 0 ? void 0 : item.classList.add(OPEN);
};
const RocheNavigationMobile$1 = /*@__PURE__*/ proxyCustomElement(class RocheNavigationMobile extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.trackLinkClick = (event) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            event.preventDefault();
            const targetLink = ((_a = event.target) === null || _a === void 0 ? void 0 : _a.getAttribute('href'))
                ? (_b = event.target) === null || _b === void 0 ? void 0 : _b.getAttribute('href')
                : (_d = (_c = event.target) === null || _c === void 0 ? void 0 : _c.parentElement) === null || _d === void 0 ? void 0 : _d.getAttribute('href');
            const targetText = ((_e = event.target) === null || _e === void 0 ? void 0 : _e.innerText)
                ? (_f = event.target) === null || _f === void 0 ? void 0 : _f.innerText
                : (_h = (_g = event.target) === null || _g === void 0 ? void 0 : _g.parentElement) === null || _h === void 0 ? void 0 : _h.innerText;
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { additionalParameters: {
                    current_location: HistoryService.getActiveUrl().pathname,
                    link_text: targetText,
                }, value: targetLink }));
            const adobeTrackingData = {
                innerText: targetText,
                url: targetLink,
                position: 'Header',
                type: 'Link',
                socialMedia: '',
                socialInteraction: '',
            };
            AdobeTracking(adobeTrackingData, 'defaultLinkClick');
        };
        this.menuId = 'roche-offcanvas-menu';
        this.worldWideLink = undefined;
        this.translations = undefined;
    }
    async componentWillLoad() {
        this.translations = {
            worldwide: await TranslationService.translate('worldwide'),
        };
        this.handleClick = this.handleClick.bind(this);
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'navigation_click',
            component_name: 'roche-navigation',
        };
    }
    componentDidLoad() {
        this.handleActivePageVisibility();
        DomService.handleTextDirection(this.element);
    }
    /* We trigger clicks where relevant, so the navigation is closed when the user first sees it */
    componentDidRender() {
        const [head, ...tail] = Array.from(this.element.querySelectorAll('nav > ul > li'));
        [...tail, head, head]
            .forEach((item) => item === null || item === void 0 ? void 0 : item.click());
    }
    render() {
        return (h(Host, { key: 'bc599be76364675fe29bd3e633076e8e2e6725a6' }, h("div", { key: 'e5e16ef7d5de2644fa2e94e13964ef8510d5358b', class: "header" }, h("roche-offcanvas-trigger", { key: '2cc410367d2c716a09c44a2692fc6c0331212d49', class: "header__close", "offcanvas-id": this.menuId, "tracking-event-name": "navigation_offcanvas_click" }, h("roche-icon", { key: '1c4dbe99a9e5848760e4b10654d346d86c44fac0', icon: "mobile-close", height: `${remCalc(32)}rem` })), this.worldWideLink
            && h("a", { class: "header__link", href: this.worldWideLink, onClick: this.trackLinkClick }, h("roche-icon", { class: "header__link-icon", icon: "globe", height: `${remCalc(24)}rem` }), h("span", null, StringService.capitalize(this.translations.worldwide)))), h("nav", { key: 'fd3db281d8fe547aa809b73fe77c130b52934897', onClick: this.handleClick }, h("slot", { key: 'a96bc89e1fdfa2730c537c5985fb0c8c90a6e592' }))));
    }
    handleClick(event) {
        if (!event.composedPath) {
            return;
        }
        const [listItem, list, navigation] = event.composedPath();
        if (!isNavigationHtmlElement(navigation)) {
            return;
        }
        this.manageListVisibility(listItem, list);
    }
    // eslint-disable-next-line class-methods-use-this
    manageListVisibility(listItem, list) {
        if (listItem.classList.contains(OPEN)) {
            closeItem(listItem);
            return;
        }
        list
            .querySelectorAll(`.${OPEN}`)
            .forEach(closeItem);
        openItem(listItem);
    }
    handleActivePageVisibility() {
        const activePage = this.element.querySelector('.current-page');
        /**
         * The style attribute is being used because the open class is not defined on componentDidLoad
         * and also to avoid adding another attribute or id to the DOM
         * */
        const activePageParent = activePage === null || activePage === void 0 ? void 0 : activePage.closest('[style]');
        if (!activePage) {
            return;
        }
        openItem(activePageParent);
    }
    get element() { return this; }
    static get style() { return RocheNavigationMobileStyle0; }
}, [4, "roche-navigation-mobile", {
        "menuId": [1, "menu-id"],
        "worldWideLink": [1, "world-wide-link"],
        "translations": [32]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-navigation-mobile", "roche-icon", "roche-offcanvas-trigger"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-navigation-mobile":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheNavigationMobile$1);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-offcanvas-trigger":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheNavigationMobile = RocheNavigationMobile$1;
const defineCustomElement = defineCustomElement$1;

export { RocheNavigationMobile, defineCustomElement };

//# sourceMappingURL=roche-navigation-mobile.js.map